import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Eye, EyeOff, Search, GripVertical } from 'lucide-react';
import { adminGetFAQs, adminCreateFAQ, adminUpdateFAQ, adminDeleteFAQ } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const emptyFAQ = {
  question_de: '',
  question_en: '',
  answer_de: '',
  answer_en: '',
  sort_order: 0,
  is_active: true,
};

export default function AdminFAQs() {
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [formData, setFormData] = useState(emptyFAQ);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchFAQs = async () => {
    try {
      const res = await adminGetFAQs();
      setFaqs(res.data);
      setFilteredFaqs(res.data);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      toast.error('Gabim gjatë marrjes së FAQ-ve');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredFaqs(faqs);
      return;
    }
    const filtered = faqs.filter(f => 
      f.question_de.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.answer_de.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredFaqs(filtered);
  }, [searchTerm, faqs]);

  const handleOpenCreate = () => {
    setSelectedFaq(null);
    setFormData({
      ...emptyFAQ,
      sort_order: faqs.length + 1,
    });
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (faq) => {
    setSelectedFaq(faq);
    setFormData({
      ...faq,
      question_en: faq.question_en || '',
      answer_en: faq.answer_en || '',
    });
    setIsDialogOpen(true);
  };

  const handleOpenDelete = (faq) => {
    setSelectedFaq(faq);
    setIsDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (selectedFaq) {
        await adminUpdateFAQ(selectedFaq.id, formData);
        toast.success('FAQ u përditësua me sukses!');
      } else {
        await adminCreateFAQ(formData);
        toast.success('FAQ u krijua me sukses!');
      }
      setIsDialogOpen(false);
      fetchFAQs();
    } catch (err) {
      console.error('Error saving FAQ:', err);
      toast.error('Gabim gjatë ruajtjes së FAQ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedFaq) return;

    try {
      await adminDeleteFAQ(selectedFaq.id);
      toast.success('FAQ u fshi me sukses!');
      setIsDeleteDialogOpen(false);
      fetchFAQs();
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      toast.error('Gabim gjatë fshirjes së FAQ');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-faqs-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">FAQ</h1>
          <p className="text-[#71717A]">Menaxho pyetjet e shpeshta</p>
        </div>
        <Button 
          onClick={handleOpenCreate}
          className="bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A]"
          data-testid="add-faq-button"
        >
          <Plus size={18} className="mr-2" />
          Shto FAQ
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
        <Input
          type="text"
          placeholder="Kërko FAQ..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-faqs"
        />
      </div>

      {/* FAQs Table */}
      <div className="bg-white rounded-sm border border-[#E4E4E7] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full admin-table">
            <thead>
              <tr>
                <th className="text-left px-6 py-4 w-12">#</th>
                <th className="text-left px-6 py-4">Pyetja (DE)</th>
                <th className="text-left px-6 py-4">Statusi</th>
                <th className="text-left px-6 py-4">Krijuar</th>
                <th className="text-right px-6 py-4">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {filteredFaqs.map((faq) => (
                <tr key={faq.id} className="border-t border-[#E4E4E7]">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-[#71717A]">
                      <GripVertical size={16} />
                      {faq.sort_order}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-[#0A0A0A] mb-1">{faq.question_de}</div>
                    <div className="text-sm text-[#71717A] truncate max-w-md">{faq.answer_de}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${
                      faq.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {faq.is_active ? <Eye size={12} /> : <EyeOff size={12} />}
                      {faq.is_active ? 'Aktiv' : 'Joaktiv'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#71717A]">{formatDate(faq.created_at)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(faq)}
                        className="text-[#71717A] hover:text-[#D4B982]"
                        data-testid={`edit-faq-${faq.id}`}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDelete(faq)}
                        className="text-[#71717A] hover:text-red-500"
                        data-testid={`delete-faq-${faq.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredFaqs.length === 0 && (
          <div className="p-12 text-center">
            <p className="text-[#71717A]">
              {searchTerm ? 'Nuk u gjet asnjë FAQ' : 'Nuk ka FAQ ende'}
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {selectedFaq ? 'Ndrysho FAQ' : 'Shto FAQ të Ri'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {/* German Section */}
            <div className="border-b border-[#E4E4E7] pb-4 mb-4">
              <h3 className="text-lg font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
                🇩🇪 Gjermanisht (Kryesore)
              </h3>
              
              <div>
                <Label htmlFor="question_de">Pyetja (DE) *</Label>
                <Input
                  id="question_de"
                  value={formData.question_de}
                  onChange={(e) => setFormData({ ...formData, question_de: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="faq-question"
                />
              </div>

              <div className="mt-4">
                <Label htmlFor="answer_de">Përgjigja (DE) *</Label>
                <Textarea
                  id="answer_de"
                  value={formData.answer_de}
                  onChange={(e) => setFormData({ ...formData, answer_de: e.target.value })}
                  required
                  rows={5}
                  className="mt-1"
                  data-testid="faq-answer"
                />
              </div>
            </div>

            {/* English Section */}
            <div className="border-b border-[#E4E4E7] pb-4 mb-4">
              <h3 className="text-lg font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
                🇬🇧 Anglisht (Opsionale)
              </h3>
              
              <div>
                <Label htmlFor="question_en">Pyetja (EN)</Label>
                <Input
                  id="question_en"
                  value={formData.question_en}
                  onChange={(e) => setFormData({ ...formData, question_en: e.target.value })}
                  className="mt-1"
                  placeholder="Question in English"
                  data-testid="faq-question-en"
                />
              </div>

              <div className="mt-4">
                <Label htmlFor="answer_en">Përgjigja (EN)</Label>
                <Textarea
                  id="answer_en"
                  value={formData.answer_en}
                  onChange={(e) => setFormData({ ...formData, answer_en: e.target.value })}
                  rows={5}
                  className="mt-1"
                  placeholder="Answer in English"
                  data-testid="faq-answer-en"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="sort_order">Renditja</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                className="mt-1 w-24"
                min={0}
              />
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">FAQ aktiv</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="flex-1"
              >
                Anulo
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A]"
                data-testid="faq-submit"
              >
                {isSubmitting ? 'Duke ruajtur...' : (selectedFaq ? 'Ruaj Ndryshimet' : 'Krijo FAQ')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmo Fshirjen</AlertDialogTitle>
            <AlertDialogDescription>
              Jeni të sigurt që dëshironi të fshini këtë FAQ? 
              Ky veprim nuk mund të kthehet prapa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulo</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-faq"
            >
              Fshi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
